import * as React from 'react'

import { F0Button } from '@factorialco/f0-react'
import { UpsellGlpButton, UpsellLandingPage } from 'generated/resources/datatable.graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useButtonActions from 'modules/discover/hooks/useButtonActions'
import useActiveSubscriptionOverload from 'modules/marketplace_modules/hooks/useActiveSubscriptionOverload'

interface GlpButtonBlockProps {
  block: UpsellGlpButton
  slug: string
  landingPage: UpsellLandingPage
}

export const GlpButtonBlock: React.FC<GlpButtonBlockProps> = ({ block, slug, landingPage }) => {
  const { company } = useCurrentSession()
  const [isButtonLoading, setIsButtonLoading] = React.useState(false)
  const { addLineItemMutation, handleSubscriptionOverloadMutation } = useButtonActions({
    billingAddon: landingPage?.moduleRelated?.billingAddonReference?.[0] || null,
    moduleName: landingPage?.moduleRelated?.chargebeeModuleName || '',
    origin: `Discover - ${slug} landing`,
    featureId: landingPage?.featureAvailabilityId || '',
    subscriptionExpiresIn: block?.subscriptionExpiresIn || '',
    chargebeeAddonId: block?.chargebeeAddonId || '',
  })

  const { activeSubscriptionOverload } = useActiveSubscriptionOverload([company.id])

  if (!block) {
    return null
  }

  const actionType = block?.actionType || 'redirect'

  const handleAddLineItem = async () => {
    if (isButtonLoading) return

    try {
      setIsButtonLoading(true)

      const result = await addLineItemMutation()
      if (result) {
        track(trackingEvents.upsell.addedLineItem, {
          source: `discover-${slug} landing`,
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.success'),
        })
        window.location.reload()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
      })
    } finally {
      setIsButtonLoading(false)
    }
  }

  const handleSubscriptionOverload = async () => {
    if (isButtonLoading) return

    try {
      setIsButtonLoading(true)
      const result = await handleSubscriptionOverloadMutation()

      if (result) {
        track(trackingEvents.upsell.addedLineItem, {
          source: `discover-${slug} landing`,
        })
        app.setFlash({
          type: 'correct',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.success'),
        })
        if (block?.url) {
          window.location.href = block?.url
        } else {
          window.location.reload()
        }
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('growth.landing_creator.buttons.add_line_item.error'),
      })
    } finally {
      setIsButtonLoading(false)
    }
  }

  const handleLabel = () => {
    if (
      actionType === 'subscription_overload' &&
      activeSubscriptionOverload?.addonIds.includes(block?.chargebeeAddonId || '')
    ) {
      return block?.secondaryLabel
    }
    return block?.label
  }

  const handleButtonClick = () => {
    track(trackingEvents.discover.clickGlpButton, {
      component: 'glp_button',
      action: block.label,
    })

    if (actionType === 'redirect') {
      if (block?.opensInNewTab) {
        window.open(block?.url, '_blank')
      } else {
        window.location.href = block?.url
      }
    } else if (actionType === 'add_line_item') {
      handleAddLineItem()
    } else if (actionType === 'subscription_overload') {
      handleSubscriptionOverload()
    }
  }

  const handleButtonDisabled = () => {
    if (actionType === 'subscription_overload') {
      return activeSubscriptionOverload?.addonIds.includes(block?.chargebeeAddonId || '')
    }
    return false
  }

  return (
    <F0Button
      key={block?.id}
      label={handleLabel()}
      variant={block?.style == 'primary_btn' ? 'default' : 'outline'}
      onClick={handleButtonClick}
      loading={isButtonLoading && actionType === 'add_line_item'}
      disabled={handleButtonDisabled()}
    />
  )
}
